<template>
  <div class="h-set">
    <div class="h-set__content">
      <div>
        <h1 class="h-set__title">Settings</h1>
        <p class="h-set__sub">How Hermes is set up for this business.</p>
      </div>

      <template v-if="canManageGateways">
        <HermesEmptyState
          v-if="error"
          variant="error"
          title="Couldn't load how you get paid"
          :message="error"
          @retry="load"
        />

        <HermesEmptyState v-else-if="loading" variant="loading" title="Loading" :skeleton-rows="4" />

        <template v-else>
          <section id="getting-paid" class="h-set__card">
            <h2>Getting paid</h2>
            <p v-if="notice" class="h-set__ok" role="status">{{ notice }}</p>
            <div>
              <div v-for="row in accounts" :key="row.key" class="h-set__row">
                <div>
                  <b>{{ row.name }}</b>
                  <div class="h-set__help">{{ row.help }}</div>
                </div>
                <div class="h-set__rowend">
                  <HermesStatusBadge :role="row.role" :label="row.badge" />
                  <button type="button" class="h-set__btn" @click="openConnect(row)">{{ row.action }}</button>
                </div>
              </div>
            </div>
            <div v-if="productsOnMain.length" class="h-set__own">
              <label class="h-set__help" for="h-set-own">Give a product its own account</label>
              <div class="h-set__ownrow">
                <select id="h-set-own" v-model="ownProductId" class="h-set__input">
                  <option v-for="p in productsOnMain" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
                <button type="button" class="h-set__btn" @click="openOwn">Connect</button>
              </div>
            </div>
          </section>

          <section class="h-set__card">
            <h2>Is everything working?</h2>
            <div>
              <div v-for="row in health" :key="row.key" class="h-set__row">
                <div>
                  <b>{{ row.title }}</b>
                  <div class="h-set__help">{{ row.help }}</div>
                </div>
                <HermesStatusBadge :role="row.role" :label="row.badge" />
              </div>
            </div>
          </section>
        </template>
      </template>

      <section v-if="canManageApiKeys" id="your-app" class="h-set__card">
        <h2>Connect your app</h2>
        <p class="h-set__help">Your app uses a key to talk to Hermes. Each key belongs to one product.</p>
        <template v-if="keyProducts.length">
          <div class="h-set__ownrow">
            <select v-model="keyProductId" class="h-set__input" aria-label="Which product the key is for">
              <option v-for="p in keyProducts" :key="p.id" :value="p.id">{{ p.name }}</option>
            </select>
            <button type="button" class="h-set__btn" :disabled="minting" @click="mintKey">{{ minting ? 'Making it…' : 'Get a key' }}</button>
          </div>
          <p v-if="keyError" class="h-set__bad">{{ keyError }}</p>
          <HermesApiKeyRevealField
            v-if="newKey"
            :value="newKey"
            :label="`Key for ${newKeyFor}`"
            start-revealed
            warning="Copy this into your app now. You won't see it again. Lost it? Make a new one here at any time."
          />
        </template>
        <p v-else class="h-set__help">Add a product first, then come back for its key.</p>
      </section>

      <div v-if="links.length" class="h-set__grid">
        <router-link v-for="l in links" :key="l.href" class="h-set__tile" :to="l.href">
          <b>{{ l.title }}</b>
          <span class="h-set__help">{{ blurb(l.href) }}</span>
        </router-link>
      </div>
    </div>

    <HermesDrawer v-model="drawer.open" :title="drawerTitle" :close-on-scrim="!drawer.saving">
      <div class="h-set__form">
        <div>
          <label class="h-set__label" for="h-set-gateway">Provider</label>
          <select id="h-set-gateway" v-model="drawer.gateway" class="h-set__input">
            <option value="revolut">Revolut</option>
            <option value="stripe">Stripe</option>
          </select>
        </div>

        <fieldset class="h-set__modes">
          <legend class="h-set__label">Test or live</legend>
          <label><input v-model="drawer.mode" type="radio" value="test" /> Test, no real money</label>
          <label><input v-model="drawer.mode" type="radio" value="live" /> Live, real payments</label>
        </fieldset>

        <div>
          <label class="h-set__label" for="h-set-key">Secret key from your provider</label>
          <input
            id="h-set-key"
            v-model="drawer.secret"
            class="h-set__input"
            type="password"
            placeholder="Paste it here"
            autocomplete="new-password"
            spellcheck="false"
          />
          <p class="h-set__help">Stored encrypted. You won't see it again.</p>
        </div>

        <p class="h-set__info">Hermes checks the key works, then sets up {{ providerName(drawer.gateway) }}'s messages for you.</p>
        <p v-if="movesAccount" class="h-set__info">
          Payments already taken stay on the old account. Afterwards, open Products and send your prices to the new one.
        </p>
        <p v-if="drawer.error" class="h-set__bad">{{ drawer.error }}</p>
      </div>

      <template #footer>
        <button type="button" class="h-set__btn" :disabled="drawer.saving" @click="drawer.open = false">Cancel</button>
        <button type="button" class="h-set__primary" :disabled="drawer.saving || !drawer.secret.trim()" @click="connect">
          {{ drawer.saving ? 'Checking…' : 'Connect' }}
        </button>
      </template>
    </HermesDrawer>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";
import { friendlyWhen } from '@root/lib/friendlyDate';
import { useTenantStore } from '@root/store/useTenantStore';

const PROVIDERS = { revolut: 'Revolut', stripe: 'Stripe', create_pay: 'Create Pay' };

const BLURBS = {
  '/admin/subscriptions': 'Who is on which plan, and when they renew.',
  '/admin/payment-requests': 'Ask a customer for a payment, and follow it.',
  '/admin/reports': 'How income is moving, and how your licences are doing.',
  '/admin/usage': "What your customers' apps have reported.",
  '/admin/integrate': 'Add a new product and connect it, step by step.',
  '/admin/users': 'Who can sign in, and what they can do.',
  '/admin/roles': 'What each kind of team member may do.',
  '/admin/vendor': 'Your VAT registration and rate.',
};

const emptyDrawer = () => ({ open: false, saving: false, name: '', productId: null, current: null, gateway: 'revolut', mode: 'test', secret: '', error: '' });

// The Settings destination: how this business gets paid, whether that is working,
// and the way to every screen that has no destination of its own. A secret key goes
// up once, is cleared here as soon as it has, and never comes back from the API.
export default {
  name: 'HermesSettings',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    links: { type: Array, default: () => [] },
    canManageGateways: { type: Boolean, default: false },
    canManageApiKeys: { type: Boolean, default: false },
    componentData: { type: Object, default: () => ({}) },
  },
  setup() {
    return { tenantStore: useTenantStore() };
  },
  data() {
    return {
      api: useApi(),
      loading: true,
      error: null,
      state: { connections: [], products: [], messages: [], billing: {} },
      notice: '',
      drawer: emptyDrawer(),
      ownProductId: null,
      keyProducts: [],
      keyProductId: null,
      minting: false,
      keyError: '',
      newKey: '',
      newKeyFor: '',
    };
  },
  computed: {
    accounts() {
      const main = this.state.connections.find((c) => c.product_id === null) || null;
      const rows = [main ? this.accountRow(main, 'Main account') : {
        key: 'main',
        name: 'Main account',
        help: 'Used by every product without an account of its own.',
        role: 'neutral',
        badge: 'Not connected',
        action: 'Connect',
        connection: null,
        productId: null,
      }];
      this.state.connections
        .filter((c) => c.product_id !== null)
        .forEach((c) => rows.push(this.accountRow(c, c.product_name || 'A product')));
      return rows;
    },
    productsOnMain() {
      const own = new Set(this.state.connections.map((c) => c.product_id));
      return this.state.products.filter((p) => !own.has(p.id));
    },
    health() {
      const rows = this.state.messages.map((m) => this.messageRow(m));
      rows.push(this.billingRow(this.state.billing));
      return rows;
    },
    drawerTitle() {
      return `${this.drawer.name}: how do you get paid?`;
    },
    movesAccount() {
      const c = this.drawer.current;
      return !!c && (c.gateway !== this.drawer.gateway || c.mode !== this.drawer.mode);
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        this.state = await this.api.get(`${this.apiBase}/admin/payment-health`);
        this.ownProductId = this.productsOnMain[0]?.id ?? null;
      } catch (e) {
        const status = e?.response?.status;
        this.error = `Please try again.${status ? ` (${status})` : ''}`;
      } finally {
        this.loading = false;
        this.$nextTick(this.scrollToHash);
      }
    },
    async loadKeyProducts() {
      try {
        const data = await this.api.get(`${this.apiBase}/admin/products`);
        this.keyProducts = (data.items || []).map((p) => ({ id: Number(p.id), name: p.name }));
        this.keyProductId = this.keyProducts[0]?.id ?? null;
      } catch (e) {
        this.keyProducts = [];
      }
      this.$nextTick(this.scrollToHash);
    },
    scrollToHash() {
      const id = (this.$route?.hash || '').slice(1);
      if (id) document.getElementById(id)?.scrollIntoView({ block: 'start' });
    },
    accountRow(c, name) {
      const ready = c.messages_ready;
      return {
        key: `c${c.id}`,
        name,
        help: `${this.providerName(c.gateway)}, ${c.mode} account`,
        role: ready ? 'success' : 'warning',
        badge: ready ? 'Connected' : 'Not finished',
        action: ready ? 'Change' : 'Finish',
        connection: c,
        productId: c.product_id,
      };
    },
    messageRow(m) {
      const provider = this.providerName(m.gateway);
      const base = { key: `m${m.gateway}`, title: `Messages from ${provider}` };
      if (m.failed > 0) {
        return {
          ...base,
          role: 'warning',
          badge: `${m.failed} to look at`,
          help: `${m.failed} didn't go through in the last 7 days. ${provider} sends them again by itself.`,
        };
      }
      if (m.last_at) return { ...base, role: 'success', badge: 'Working', help: `Last one ${friendlyWhen(m.last_at)}` };
      return { ...base, role: 'neutral', badge: 'None yet', help: 'None have arrived yet. They start with your first payment.' };
    },
    billingRow(b) {
      const base = { key: 'billing', title: 'Monthly billing' };
      if (b.late > 0) {
        return { ...base, role: 'danger', badge: `${b.late} late`, help: 'These charges should have gone out by now. The billing run may have stopped.' };
      }
      if (b.last_charge_at) return { ...base, role: 'success', badge: 'Working', help: `Last charge ${friendlyWhen(b.last_charge_at)}` };
      if (b.due > 0) return { ...base, role: 'neutral', badge: 'Waiting', help: "The first charge hasn't come round yet." };
      return { ...base, role: 'neutral', badge: 'Nothing due', help: 'Hermes has no charges to make yet.' };
    },
    providerName(gateway) {
      return PROVIDERS[gateway] || gateway;
    },
    blurb(href) {
      return BLURBS[href] || '';
    },
    openConnect(row) {
      const c = row.connection;
      this.drawer = {
        ...emptyDrawer(),
        open: true,
        name: row.name,
        productId: row.productId,
        current: c,
        gateway: PROVIDERS[c?.gateway] && c.gateway !== 'create_pay' ? c.gateway : 'revolut',
        mode: c?.mode || 'test',
      };
    },
    openOwn() {
      const product = this.productsOnMain.find((p) => p.id === this.ownProductId);
      if (product) this.openConnect({ name: product.name, productId: product.id, connection: null });
    },
    async connect() {
      this.drawer.saving = true;
      this.drawer.error = '';
      const { gateway, mode, secret, productId } = this.drawer;
      try {
        await this.api.post(`${this.apiBase}/admin/gateway-connections/connect`, {
          gateway,
          mode,
          secret_key: secret,
          ...(productId !== null ? { product_id: productId } : {}),
        });
        this.drawer = emptyDrawer();
        this.notice = 'Connected and checked.';
        await this.load();
        this.tenantStore.setPaymentMode(this.modeOf(this.state.connections));
      } catch (e) {
        this.drawer.saving = false;
        this.drawer.error = e?.response?.data?.message || 'That did not work. Nothing has changed.';
      }
    },
    modeOf(connections) {
      const modes = [...new Set(connections.map((c) => c.mode))];
      if (!modes.length) return 'none';
      return modes.length === 1 ? modes[0] : 'mixed';
    },
    async mintKey() {
      const product = this.keyProducts.find((p) => p.id === this.keyProductId);
      if (!product) return;
      this.minting = true;
      this.keyError = '';
      this.newKey = '';
      try {
        const data = await this.api.post(`${this.apiBase}/admin/api-keys`, { name: `${product.name} key`, product_id: product.id });
        this.newKey = data.api_key;
        this.newKeyFor = product.name;
      } catch (e) {
        this.keyError = e?.response?.data?.message || 'That did not work. Please try again.';
      } finally {
        this.minting = false;
      }
    },
  },
  mounted() {
    if (this.canManageGateways) this.load();
    if (this.canManageApiKeys) this.loadKeyProducts();
  },
};
</script>

<style scoped>
.h-set { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-set__content { max-width: 1040px; margin: 0 auto; padding: 26px 32px 64px; display: grid; gap: 20px; }
.h-set__content > * { min-width: 0; }
.h-set__title { font-size: 25px; font-weight: 700; letter-spacing: -0.02em; margin: 0; }
.h-set__sub { font-size: 15px; color: var(--h-text-secondary, #64748B); margin: 2px 0 0; }

.h-set__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); padding: 18px; display: grid; gap: 12px; min-width: 0; scroll-margin-top: 16px; }
.h-set__card h2 { font-size: 16px; font-weight: 600; margin: 0; }
.h-set__help { font-size: 13px; color: var(--h-text-secondary, #64748B); margin: 0; font-weight: 400; }

.h-set__row { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; padding: 11px 0; border-top: 1px solid var(--h-border, #E2E8F0); }
.h-set__row:first-of-type { border-top: 0; }
.h-set__row > div:first-child { min-width: 0; flex: 1 1 220px; }
.h-set__row b { font-weight: 600; }
.h-set__rowend { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; }

.h-set__own { display: grid; gap: 6px; border-top: 1px solid var(--h-border, #E2E8F0); padding-top: 12px; }
.h-set__ownrow { display: flex; flex-wrap: wrap; gap: 10px; }
.h-set__ownrow .h-set__input { flex: 1 1 200px; width: auto; min-width: 0; max-width: 360px; }

.h-set__btn { font: inherit; font-size: 14px; font-weight: 600; color: var(--h-accent-dark, #0369A1); background: none; border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px); padding: 7px 14px; cursor: pointer; }
.h-set__btn:hover:not(:disabled) { background: var(--h-bg-subtle, #F1F5F9); }
.h-set__primary { font: inherit; font-size: 14px; font-weight: 600; color: var(--h-text-inverse, #fff); background: var(--h-accent-hover, #0284C7); border: 1px solid transparent; border-radius: var(--h-radius, 6px); padding: 7px 14px; cursor: pointer; }
.h-set__btn:focus-visible, .h-set__primary:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-set__btn:disabled, .h-set__primary:disabled { opacity: 0.55; cursor: not-allowed; }

.h-set__grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr)); gap: 12px; }
.h-set__tile { display: grid; gap: 4px; align-content: start; background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); padding: 16px 18px; color: var(--h-text-primary, #0F172A); text-decoration: none; }
.h-set__tile:hover { border-color: var(--h-accent, #0EA5E9); }
.h-set__tile:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-set__tile b { font-weight: 600; }

.h-set__form { display: grid; gap: 16px; }
.h-set__label { display: block; font-size: 14px; font-weight: 500; margin-bottom: 4px; padding: 0; }
.h-set__input { width: 100%; box-sizing: border-box; font: inherit; font-size: 14px; color: var(--h-text-primary, #0F172A); background: var(--h-bg-card, #fff); border: 1px solid var(--h-border-strong, #CBD5E1); border-radius: var(--h-radius, 6px); padding: 8px 10px; }
.h-set__input:focus { outline: none; border-color: var(--h-accent, #0EA5E9); box-shadow: var(--h-focus-ring-soft); }
.h-set__modes { border: 0; margin: 0; padding: 0; display: grid; gap: 6px; font-size: 14px; }
.h-set__modes label { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.h-set__modes input { accent-color: var(--h-accent, #0EA5E9); }

.h-set__info { margin: 0; font-size: 13.5px; background: var(--h-accent-light, #E0F2FE); color: var(--h-accent-dark, #0369A1); border-radius: 8px; padding: 10px 12px; }
.h-set__ok { margin: 0; font-size: 13.5px; background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); border-radius: 8px; padding: 10px 12px; }
.h-set__bad { margin: 0; font-size: 13.5px; background: var(--h-danger-bg, #FEF2F2); color: var(--h-danger-text, #B91C1C); border-radius: 8px; padding: 10px 12px; }

@media (max-width: 720px) {
  .h-set__content { padding: 20px 16px 48px; }
}
</style>
